// Shared pieces: icons, chips, KPI tiles. Icons are stroke SVG on a 24 grid so
// they scale and recolour, per the design system.

export function Icon({ name, ...rest }) {
  const paths = {
    accounts: <><rect x="3" y="4" width="18" height="16" /><path d="M3 9h18M9 9v11" /></>,
    contacts: <><circle cx="12" cy="8" r="4" /><path d="M4 21v-2a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v2" /></>,
    doc: <><path d="M4 4h16v16H4z" /><path d="M8 9h8M8 13h5" /></>,
    intel: <><path d="M4 5h16v14H4z" /><path d="M7 9h6M7 13h10M7 16h8" /></>,
    scan: <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" />,
    bolt: <path d="M13 3L5 14h6l-2 7 8-11h-6z" />,
    mail: <><path d="M3 6h18v12H3z" /><path d="M3 7l9 6 9-6" /></>,
    reply: <path d="M4 4h16v12H8l-4 4z" />,
    brand: <><path d="M5 21V5l7-2 7 2v16" /><path d="M9 21v-6h6v6" /></>,
    chevron: <path d="M4 8l8 8 8-8" />,
    right: <path d="M9 5l7 7-7 7" />,
    search: <><circle cx="11" cy="11" r="7" /><path d="M16 16l5 5" /></>,
    spark: <><path d="M12 3v3M12 18v3M3 12h3M18 12h3" /><circle cx="12" cy="12" r="3" /></>,
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="square" width="16" height="16" {...rest}>
      {paths[name] || null}
    </svg>
  )
}

export const Chip = ({ kind, children }) => <span className={`chip ${kind || 'plain'}`}>{children}</span>

// A focus area's state. Named/mentioned/absent is a scored scale; unresearched
// is the absence of a reading, so it gets a dashed outline rather than a colour.
export function AreaChip({ status }) {
  const letter = { named: 'N', mentioned: 'M', absent: '—', unresearched: '?' }[status] || '—'
  return <span className={`chip ${status}`} title={status}>{letter}</span>
}

export function Kpi({ label, value, sub, tone, subTone }) {
  return (
    <div className="kpi-tile">
      <div className="kpi-label">{label}</div>
      <div className={`kpi-value mono ${tone || ''}`}>{value}</div>
      {sub ? <div className={`kpi-sub ${subTone || ''}`}>{sub}</div> : null}
    </div>
  )
}

export const Empty = ({ children }) => <div className="empty">{children}</div>

export function ErrorNote({ error }) {
  if (!error) return null
  return <div className="err">{error.message || String(error)}</div>
}

// Every figure is shown in USD so the columns compare, with the currency the
// company actually reported on hover. Converted values are marked, because a
// conversion is a derived number and the research standard cares about that.
export function Money({ value, fallback }) {
  if (!value || value.usd_display == null) {
    return <span style={{ color: 'var(--tertiary)' }}>{fallback || '—'}</span>
  }
  if (!value.converted) return <span title={value.note}>{value.usd_display}</span>
  return (
    // USD on top, what the company actually reported underneath. Side by side
    // the two figures ran the column too wide to read.
    <span title={value.note} style={{ display: 'inline-block', lineHeight: 1.25 }}>
      <span style={{ borderBottom: '1px dotted var(--tertiary)' }}>{value.usd_display}</span>
      <span style={{ display: 'block', color: 'var(--tertiary)', fontSize: 'var(--fs-badge)' }}>
        {value.local_display}
      </span>
    </span>
  )
}

// 1 is highest. Unset is a real state, not a 6.
// An inferred address is a guess built from a company pattern. It must never
// look like a verified one: dotted underline, muted, and the derivation on
// hover. A verified or provider-supplied address renders plainly.
export function Email({ contact }) {
  if (!contact?.email) return <span style={{ color: 'var(--tertiary)' }}>&mdash;</span>
  if (contact.email_status === 'verified' || contact.email_status === 'provider') return <span>{contact.email}</span>
  return (
    <span
      title={`Unverified. Derived from ${contact.email_source || contact.email_pattern?.pattern || 'the company pattern'}. Confirm before sending.`}
      style={{ color: 'var(--warn)', borderBottom: '1px dotted var(--warn)', cursor: 'help' }}
    >
      {contact.email}
      <span className="mono" style={{ fontSize: '9px', marginLeft: 4, verticalAlign: 'super' }}>?</span>
    </span>
  )
}

export function PriorityPicker({ value, onChange }) {
  return (
    <select
      className={`prio ${value ? 'set' : ''}`}
      value={value ?? ''}
      // A row opens the account on click, so every pointer event on the control
      // has to stop there or the panel steals the interaction mid-choice.
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation()
        onChange(e.target.value === '' ? null : Number(e.target.value))
      }}
      aria-label="Priority"
      title="Priority, 1 is highest"
    >
      <option value="">Set…</option>
      {[1, 2, 3, 4, 5].map((n) => (
        <option key={n} value={n}>
          P{n}
          {n === 1 ? ' highest' : n === 5 ? ' lowest' : ''}
        </option>
      ))}
    </select>
  )
}

// Choosing the model is a real cost lever, so it is shown where the cost is.
//
// It genuinely controls drafting, which this app runs itself. For research and
// top-up runs the app only writes a brief for an agent to run, so the choice
// there sets the estimate and is written into the brief. It is a recorded
// intent, not something the app can enforce.
export function ModelPicker({ value, options, onChange, compare, count }) {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="row" style={{ flexWrap: 'wrap' }}>
        <span className="lbl">Model</span>
        {(options || []).map((m) => (
          <button key={m.id} className={`btn ${value === m.id ? 'on' : ''}`} onClick={() => onChange(m.id)} title={m.price}>
            {m.label}
          </button>
        ))}
      </div>
      {compare?.length && count ? (
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          {compare.map((c) => (
            <span key={c.model} className="mono" style={{ fontSize: 'var(--fs-badge)', color: c.model === value ? 'var(--accent-ink)' : 'var(--tertiary)', fontWeight: c.model === value ? 600 : 400 }}>
              {c.model_label} {c.usd_display}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}
